import React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PageContainer({ className = '', children }) {
  return (
    <div className={cn('min-h-screen bg-[#0a0e14] text-slate-100', className)}>
      <main className="admin-page-surface mx-auto max-w-[1680px] px-4 py-5 lg:pl-72 lg:pr-8 lg:py-7">{children}</main>
    </div>
  );
}

export function Card({ className = '', interactive = false, children }) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-cyan-400/30 bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] backdrop-blur-md shadow-[0_0_28px_-16px_rgba(34,211,238,0.8)] transition-all duration-300',
        interactive && 'hover:-translate-y-0.5 hover:scale-[1.01] hover:shadow-[0_0_36px_-12px_rgba(34,211,238,0.95)]',
        className,
      )}
    >
      {children}
    </section>
  );
}

const buttonVariants = {
  primary:
    'border-cyan-300/50 bg-gradient-to-r from-cyan-500 to-sky-500 text-slate-950 shadow-[0_0_24px_-8px_rgba(34,211,238,0.95)] hover:scale-[1.01] hover:shadow-[0_0_32px_-8px_rgba(34,211,238,1)]',
  secondary:
    'border-cyan-300/35 bg-[#0b162a] text-cyan-100 hover:scale-[1.01] hover:bg-cyan-500/15 hover:shadow-[0_0_24px_-10px_rgba(34,211,238,0.9)]',
  danger:
    'border-rose-400/40 bg-gradient-to-r from-rose-500 to-red-500 text-white hover:scale-[1.01] hover:shadow-[0_0_26px_-10px_rgba(244,63,94,0.95)]',
};

export function Button({ variant = 'primary', className = '', children, ...props }) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 disabled:opacity-50 disabled:pointer-events-none',
        buttonVariants[variant] || buttonVariants.primary,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

const badgeStyles = {
  online: 'border-emerald-400/45 bg-emerald-500/20 text-emerald-200',
  offline: 'border-rose-400/45 bg-rose-500/20 text-rose-200',
  warning: 'border-amber-400/45 bg-amber-500/20 text-amber-200',
};

export function Badge({ variant = 'online', children, className = '' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
        badgeStyles[variant] || badgeStyles.online,
        className,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full animate-pulse bg-current" />
      {children}
    </span>
  );
}

export function Input({ className = '', ...props }) {
  return (
    <input
      className={cn(
        'h-11 w-full rounded-xl border border-cyan-300/30 bg-[#081427] px-3 text-sm text-slate-100 placeholder:text-slate-400/80 transition-all duration-200 outline-none focus:border-cyan-300/75 focus:shadow-[0_0_0_3px_rgba(34,211,238,0.2)]',
        className,
      )}
      {...props}
    />
  );
}

export function StatCard({
  icon: Icon,
  label,
  value,
  note,
  trend,
  badge = null,
  className = '',
}) {
  return (
    <Card interactive className={cn('p-5', className)}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="h-10 w-10 rounded-xl border border-cyan-300/35 bg-cyan-500/10 flex items-center justify-center">
          {Icon ? <Icon className="h-5 w-5 text-cyan-200" /> : null}
        </div>
        {badge}
      </div>
      <p className="text-xs uppercase tracking-[0.16em] text-cyan-200/80">{label}</p>
      <p className="mt-1 font-outfit text-3xl font-bold text-slate-50">{value}</p>
      {note ? <p className="mt-1 text-sm text-slate-300">{note}</p> : null}
      {trend ? <p className="mt-2 text-xs text-cyan-200/85">{trend}</p> : null}
    </Card>
  );
}

export function AlertBanner({ title, value, note, tone = 'warning', icon: Icon, href, className = '' }) {
  const toneClass =
    tone === 'critical'
      ? 'border-rose-400/45 bg-rose-500/12'
      : tone === 'normal'
      ? 'border-emerald-400/45 bg-emerald-500/12'
      : 'border-amber-400/45 bg-amber-500/12';

  const content = (
    <Card interactive className={cn('p-4', toneClass, className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-300">Alert Summary</p>
          <p className="mt-1 text-sm font-semibold text-slate-100">{title}</p>
        </div>
        {Icon ? <Icon className="h-5 w-5 text-cyan-200" /> : null}
      </div>
      <p className="mt-2 text-2xl font-outfit font-bold text-slate-50">{value}</p>
      <p className="text-xs text-slate-300/90">{note}</p>
    </Card>
  );

  if (href) return <a href={href}>{content}</a>;
  return content;
}

export function SearchBar({ value, onChange, placeholder = 'Cari data...', className = '' }) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cyan-200/80" />
      <Input value={value} onChange={onChange} placeholder={placeholder} className="pl-9" />
    </div>
  );
}

export function QuickAction({ icon: Icon, label, onClick, href, variant = 'secondary', className = '' }) {
  const content = (
    <Button variant={variant} onClick={onClick} className={cn('w-full justify-start', className)}>
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {label}
    </Button>
  );
  if (href) {
    return (
      <a href={href} className="block">
        {content}
      </a>
    );
  }
  return content;
}
